import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Nav, { WHATSAPP_ORDER_URL } from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../components/Seo";

const SITE = "https://riznwash.com";
const EASE = [0.22, 1, 0.36, 1];

const REVIEWS = [
  { author: "Ananya S.", area: "Sector 43", rating: 5,
    text: "Best call I made all monsoon. Pickup on time, clothes came back smelling amazing. Cheaper than the ‘premium’ laundry we were using before." },
  { author: "Rohit K.", area: "DLF Phase 3", rating: 5,
    text: "Sneakers looked new. Genuinely surprised. Only laundry in Gurugram I trust with my AF1s." },
  { author: "Priya M.", area: "Sushant Lok", rating: 5,
    text: "Switched to RIZ N WASH permanently. The ironing is chef's kiss and delivery is always on time." },
  { author: "Vikram T.", area: "Golf Course Road", rating: 5,
    text: "Ordered ~10 times so far, zero complaints. Suit dry cleaning is on par with the 5-star hotels I've tried." },
  { author: "Divya R.", area: "Sector 56", rating: 5,
    text: "Wedding lehenga came back looking brand new. Packaging is beautiful too — feels premium." },
  { author: "Aman P.", area: "Palam Vihar", rating: 5,
    text: "I subscribed to the weekly plan and never went back to a washing machine. Underrated luxury in Gurugram." },
];

export default function BestLaundryGurugram() {
  const path = "/best-laundry-gurugram";
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "RIZ N WASH — Best laundry in Gurugram",
      url: `${SITE}${path}`,
      telephone: "+91-73032-41330",
      priceRange: "₹₹",
      address: { "@type": "PostalAddress", addressLocality: "Gurugram", addressRegion: "Haryana", addressCountry: "IN" },
      geo: { "@type": "GeoCoordinates", latitude: 28.4595, longitude: 77.0266 },
      aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1240" },
      review: REVIEWS.map((r) => ({
        "@type": "Review",
        author: { "@type": "Person", name: r.author },
        reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
        reviewBody: r.text,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "RIZ N WASH", item: SITE },
        { "@type": "ListItem", position: 2, name: "Best laundry in Gurugram", item: `${SITE}${path}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid="best-laundry-page">
      <Seo
        title="Best Laundry Service in Gurugram · Reviewed"
        description="Why 10,000+ customers in Gurugram rank RIZ N WASH the best laundry & dry cleaning service — 4.9★ rating, free pickup, 48-hour turnaround. Real reviews from every sector."
        path={path}
        jsonLd={jsonLd}
      />
      <Nav />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-12 md:pt-24 pb-12">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}
          className="overline mb-4">Best laundry in Gurugram · Reviewed by 1240 customers</motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
          className="font-display font-light text-4xl sm:text-5xl lg:text-[5.5rem] tracking-tight leading-[1.02]"
          data-testid="best-h1"
        >
          Why Gurugram calls us the <em className="italic text-rw-blue not-italic">best</em>.
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
          className="mt-6 md:mt-8 text-lg md:text-xl text-rw-ink2 max-w-2xl leading-relaxed"
        >
          A 4.9-star rating isn't luck. It's colour-sorted washes, hand-ironing, on-time pickups, and 10,000+ customers across Gurugram who stopped shopping around.
        </motion.p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href={WHATSAPP_ORDER_URL} target="_blank" rel="noopener noreferrer" className="btn-dark rounded-full px-6 py-3 text-sm md:text-base font-semibold" data-testid="best-book-btn">
            Order your first pickup on WhatsApp →
          </a>
          <Link to="/plans" className="rounded-full px-6 py-3 text-sm font-semibold border border-rw-ink/20 hover:bg-rw-ink hover:text-white transition-colors">
            See weekly plans
          </Link>
        </div>
        <div className="mt-10 flex flex-wrap gap-x-10 gap-y-4 text-sm text-rw-ink2">
          <div><span className="font-display text-2xl text-rw-ink">4.9★</span> · 1240 reviews</div>
          <div><span className="font-display text-2xl text-rw-ink">10k+</span> · orders delivered</div>
          <div><span className="font-display text-2xl text-rw-ink">33</span> · Gurugram sectors covered</div>
          <div><span className="font-display text-2xl text-rw-ink">48h</span> · avg turnaround</div>
        </div>
      </section>

      {/* Why we win */}
      <section className="bg-rw-bg2 border-y border-rw-ink/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20">
          <div className="overline mb-3">Why we rank #1</div>
          <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.05] max-w-3xl">
            Six things every other Gurugram laundry gets wrong.
          </h2>
          <ol className="mt-14 grid md:grid-cols-2 gap-8">
            {[
              ["No sorting.", "We colour-sort every load. Whites stay white, blacks stay black."],
              ["Machine-ironed shirts.", "We iron by hand on finish tables — no shine marks, no missed collars."],
              ["Late pickups.", "You get a live rider location + a call before we arrive."],
              ["Vague pricing.", "₹19/piece is ₹19/piece. No hidden 'packaging fees'."],
              ["Missing clothes.", "Every load is barcoded on pickup. Nothing gets lost, ever."],
              ["Silence.", "WhatsApp updates at pickup, wash, and delivery — never guess where your clothes are."],
            ].map(([t, d], i) => (
              <li key={i} className="rounded-2xl border border-rw-ink/10 bg-rw-bg p-6" data-testid={`best-reason-${i}`}>
                <div className="font-display text-4xl text-rw-blue">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-xl mt-3">{t}</h3>
                <p className="text-rw-ink2 text-sm mt-2">{d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Reviews */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-20">
        <div className="overline mb-3">Word on the street</div>
        <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.05]">
          Real reviews from every corner of Gurugram.
        </h2>
        <div className="grid md:grid-cols-3 gap-4 mt-12">
          {REVIEWS.map((r, i) => (
            <div key={i} className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6" data-testid={`best-review-${i}`}>
              <div className="text-rw-blue text-lg" aria-label={`${r.rating} out of 5 stars`}>{"★".repeat(r.rating)}</div>
              <p className="mt-3 text-rw-ink2 leading-relaxed">"{r.text}"</p>
              <div className="mt-6 overline text-rw-ink3">{r.author} · {r.area}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-20">
        <div className="rounded-2xl border border-rw-ink/10 bg-rw-ink text-white p-10 md:p-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="overline text-rw-blue mb-3">Ready to switch?</div>
            <h3 className="font-display text-3xl md:text-5xl tracking-tight leading-[1.05]">
              Try the best laundry in Gurugram — risk-free.
            </h3>
            <p className="mt-4 text-white/70 max-w-lg">First pickup unhappy? Full refund, no questions. We're that sure.</p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <a href={WHATSAPP_ORDER_URL} target="_blank" rel="noopener noreferrer" className="btn-dark bg-white text-rw-ink hover:bg-rw-blue rounded-full px-6 py-3 font-semibold text-base" data-testid="best-cta-book">
              Order on WhatsApp →
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
